// JOBE-0044.13: whyThisRow — pure helper that takes the chip array and
// a selected EntityRow and emits per-chip match descriptors for the
// "Why this row · filter trace" inspector mode (umbrella §R8 mode-swap,
// design-handoff §Direction B).
//
// The match check is **client-side, advisory**. The server already
// filtered the row, so a chip is expected to match — the inspector
// surfaces *why*. Defensive `✗` rows are possible for cross-facet
// edge cases (per umbrella §"Inspector — Why this row mode"), but the
// row is shown either way; the matcher just classifies the chip.
//
// Only `predicate`-kind chips translate into trace rows. `scope` chips
// (the locked "anyone you can read" or anchor scopes) are filtered out
// by the caller — they're not user-removable so showing them in the
// trace is noise.
//
// Each output row carries:
//   - `friendlyToken`  — the user-visible chip token (e.g. "type",
//     "owner", "since").
//   - `wirePredicate`  — the query_entities predicate the BFF sees
//     (e.g. "type", "assignedTo", "committedAt>=").
//   - `values`         — the chip's value list, copied through.
//   - `status`         — 'matched' | 'unmatched' | 'unknown'.
//     'unknown' covers predicates not exposed on `EntityRow` (e.g.
//     `procedure:`, `cites:`, `from:` — see chipVocabulary `bffWired:
//     false` rows). The chip is still translated to wire form, but the
//     matcher can't confirm the row from the list payload alone.
//   - `detail`         — the value-side string for the bullet's
//     right-hand cell (e.g. "report", "alice · person",
//     "after 2026-04-01"). Display-only; the matcher itself runs on
//     URNs / kinds, not display labels.

// Map each wirePredicate to a `(row, values) => { matched, detail }`
// resolver. `null` resolvers signal "unknown — row payload doesn't
// carry this predicate's field". Resolvers return:
//   - matched: boolean — whether the row satisfies the chip.
//   - detail:  string  — the right-side bullet text.
const RESOLVERS = {
  type: (row, values) => {
    const k = row?.kind || '';
    return {
      matched: values.includes(k),
      detail: k || '(no kind)',
    };
  },
  assignedTo: (row, values) => {
    // chipVocabulary maps both `owner:` and `by:` onto the row's
    // `owner` field; the wire-side distinction (assignedTo vs
    // committedBy) is preserved in the predicate but, on
    // `EntityRow`, both resolve to `owner.urn`. The list endpoint
    // surfaces the same identity for both predicates per
    // JOBE-0044.15.
    return resolveOwner(row, values);
  },
  committedBy: (row, values) => resolveOwner(row, values),
  inProject: (row, values) => {
    const urn = row?.project?.urn || '';
    return {
      matched: !!urn && values.includes(urn),
      detail: row?.project?.name
        || row?.project?.urn
        || '(no project)',
    };
  },
  inCollection: (row, values) => {
    const cols = Array.isArray(row?.collections) ? row.collections : [];
    const urns = cols.map((c) => c?.urn).filter(Boolean);
    const hit = urns.some((u) => values.includes(u));
    const names = cols.map((c) => c?.name || c?.urn).filter(Boolean);
    return {
      matched: hit,
      detail: names.length > 0 ? names.join(', ') : '(no collections)',
    };
  },
  'committedAt>=': (row, values) => {
    const v = values[0];
    const t = row?.lastChangedAt;
    return {
      matched: !!(v && t && Date.parse(t) >= Date.parse(v)),
      detail: t ? `last changed ${t}` : '(no timestamp)',
    };
  },
  'committedAt<': (row, values) => {
    const v = values[0];
    const t = row?.lastChangedAt;
    return {
      matched: !!(v && t && Date.parse(t) < Date.parse(v)),
      detail: t ? `last changed ${t}` : '(no timestamp)',
    };
  },
  q: (row, values) => {
    // Free-text predicate. The BFF runs a server-side search; the
    // inspector approximates by checking the row's projected name +
    // summary for any of the query tokens. Case-insensitive substring.
    const haystack = [row?.name || '', row?.summary || '']
      .join('  ')
      .toLowerCase();
    const tokens = values
      .flatMap((v) => String(v || '').split(/\s+/))
      .filter(Boolean)
      .map((t) => t.toLowerCase());
    const hit = tokens.length === 0
      || tokens.some((t) => haystack.includes(t));
    return {
      matched: hit,
      detail: tokens.length > 0
        ? `tokens: ${tokens.join(', ')}`
        : '(empty query)',
    };
  },
};

function resolveOwner(row, values) {
  const urn = row?.owner?.urn || '';
  return {
    matched: !!urn && values.includes(urn),
    detail: row?.owner?.displayName
      || row?.owner?.urn
      || '(no owner)',
  };
}

export function whyThisRow(chips, row) {
  if (!Array.isArray(chips) || !row) return [];
  const out = [];
  for (const chip of chips) {
    if (!chip || chip.kind !== 'predicate') continue;
    const wire = chip.wirePredicate;
    const values = Array.isArray(chip.values) ? chip.values : [];
    const resolver = RESOLVERS[wire];
    if (!resolver) {
      // Predicate exists in chip vocabulary but the row payload
      // doesn't carry the field (bffWired: false predicates like
      // `procedure:` / `cites:` / `from:`). Surface as "unknown" so
      // the user sees the chip in the trace without a misleading
      // ✓/✗.
      out.push({
        friendlyToken: chip.friendlyToken || wire || '?',
        wirePredicate: wire || '?',
        values,
        status: 'unknown',
        detail: values.length > 0 ? values.join(', ') : '(no values)',
      });
      continue;
    }
    const { matched, detail } = resolver(row, values);
    out.push({
      friendlyToken: chip.friendlyToken || wire,
      wirePredicate: wire,
      values,
      status: matched ? 'matched' : 'unmatched',
      detail,
    });
  }
  return out;
}
